<template>
  <RevCard class="w-full p-32">
    <div class="mb-32 flex items-center">
      <h2 class="heading-2">
        {{ i18n(translations.title) }}
      </h2>
      <RevButton
        v-if="orderId"
        class="ml-auto"
        data-qa="contactCustomer"
        size="small"
        :to="conversationLink"
        variant="secondary"
      >
        {{ i18n(translations.contactCustomer) }}
      </RevButton>
    </div>

    <div
      v-if="isAddressAvailable"
      class="text-static-default-low flex flex-col"
    >
      <div class="flex">
        <p class="body-2">
          {{ i18n(translations.addressLabel) }}
        </p>
        <div class="grow text-right">
          <p v-for="line in address" :key="line" class="body-2">
            {{ line }}
          </p>
        </div>
      </div>
      <RevDivider class="my-20" />
    </div>

    <div v-if="isPhoneAvailable" class="flex flex-col">
      <div class="flex">
        <p class="text-static-default-low body-2">
          {{ i18n(translations.phoneNumber) }}
        </p>
        <p class="body-2-bold grow text-right">
          {{ phoneNumber }}
        </p>
      </div>
      <RevDivider class="my-20" />
    </div>

    <div v-if="trackingNumber" class="flex flex-col">
      <div class="flex">
        <p class="text-static-default-low body-2">
          {{ i18n(translations.trackingNumber) }}
        </p>
        <RevButtonBase
          v-if="trackingNumber.link.href"
          class="body-2-bold grow text-right underline"
          data-qa="tracking-number-link"
          :to="trackingNumber.link"
        >
          {{ trackingNumber.label }}
        </RevButtonBase>
        <p v-else class="body-2-bold grow text-right">
          {{ trackingNumber.label }}
        </p>
      </div>
      <RevDivider class="my-20" />
    </div>

    <div
      v-if="isidentityDocumentsFilesAvailable"
      class="text-static-default-low flex flex-col"
    >
      <div class="flex">
        <p class="body-2">
          {{ i18n(translations.identityLabel) }}
        </p>
        <div class="flex grow flex-col gap-8">
          <RevButtonBase
            v-for="(document, index) in identityDocumentsFiles"
            :key="document.label"
            class="text-static-default-low body-2 ml-auto flex items-center"
            :data-qa="`document-${index}`"
            target="_blank"
            :to="document.file_url"
          >
            <p>{{ document.label }}</p>
            <IconDownload class="ml-12" />
          </RevButtonBase>
        </div>
      </div>
      <RevDivider class="my-20" />
    </div>

    <div
      v-if="isTransferCertificateAvailable"
      class="text-static-default-low flex flex-col"
    >
      <div class="flex">
        <p class="body-2">
          {{ i18n(translations.saleVoucherLabel) }}
        </p>
        <div class="flex grow">
          <RevButtonBase
            class="body-2 ml-auto flex items-center"
            data-qa="transfer-certificate-link"
            target="_blank"
            :to="transferCertificate"
          >
            {{ i18n(translations.download) }} <IconDownload class="ml-12" />
          </RevButtonBase>
        </div>
      </div>
      <RevDivider class="my-20" />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue'

import type { LinkExternal } from '@backmarket/http-api'
import type { BuybackModelTrackingNumber } from '@backmarket/http-api/src/api-specs-buyback/models'
import type { BuybackOrderDetails } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { IconDownload } from '@ds/icons/IconDownload'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './CustomerInformation.translations'

const props = defineProps<{
  phoneNumber?: string
  address: string[]
  trackingNumber?: BuybackModelTrackingNumber
  identityDocumentsFiles: BuybackOrderDetails['identityDocuments']['documents']
  transferCertificate?: LinkExternal
  orderId: string
}>()

const i18n = useI18n()

const isAddressAvailable = computed(() => !isEmpty(props.address))
const isidentityDocumentsFilesAvailable = computed(
  () => !isEmpty(props.identityDocumentsFiles),
)
const isTransferCertificateAvailable = computed(
  () => !isEmpty(props.transferCertificate),
)
const isPhoneAvailable = computed(() => !isEmpty(props.phoneNumber))

const conversationLink = computed(() => ({
  type: 'internal',
  name: ROUTE_NAMES.BUYBACK.ORDERS.CONVERSATION,
  params: { id: props.orderId },
}))
</script>
